// domain/services/effectiveDating.js

/**
 * As-of effective dating over CALENDAR dates.
 *
 * Bounds are 'YYYY-MM-DD' strings, or null for an open bound. Comparison is lexicographic on
 * that format, which for zero-padded ISO dates is the same as chronological order — so there
 * is no time-of-day or timezone ambiguity anywhere in the effective-period logic.
 *
 * This mirrors the billing projector's own `effectiveDating` deliberately: the projector selects a
 * client's payer by running exactly this predicate over the coverages written here. Keeping the two
 * in step is what makes a coverage recorded in the UI resolve the way the user expects at
 * projection time. The projector is on a parallel branch and has no path here to cite yet.
 */

import {
  addDays as addCalendarDays,
  daysBetween as calendarDaysBetween,
  readCalendarDate,
} from 'src/kernel/time';

/**
 * Normalizes a bound to 'YYYY-MM-DD', or null for an open bound.
 *
 * Takes a STRING ONLY. A Date is refused, and that is the whole point rather than an oversight: a
 * Date is an instant, a calendar date is not, and converting one to the other needs a timezone
 * this function cannot know. Every choice is wrong for somebody —
 *
 *   `new Date(2026, 6, 1)` (local midnight, 1 July) read as UTC   → 2026-06-30 in Tokyo
 *   `new Date(Date.UTC(2026, 2, 14))` read from local parts       → 2026-03-13 in New York
 *
 * — so instead of silently picking a convention and shifting somebody's day by one, an instant is
 * unreadable here and `validateCoverage` reports it. That also sharpens the guard against a date
 * input that hands over the wrong type: a Date is now caught as loudly as a Firestore Timestamp.
 *
 * Deliberately STRICTER than the projector's mirror of this function, which slices any string
 * without checking it. Anything unrecognized has to come back as null, or `UNREADABLE_EFFECTIVE_
 * DATE` can never fire: a value like '07/01/2026' survives a bare slice intact, sorts before every
 * real date, and would push the coverage into ENDED forever with no error anywhere.
 *
 * @param {string|null|undefined} bound - 'YYYY-MM-DD' (a longer ISO string is truncated)
 * @returns {string|null}
 */
export function toDateStr(bound) {
  if (bound === null || bound === undefined) {
    return null;
  }
  if (typeof bound === 'string') {
    return readCalendarDate(bound.slice(0, 10));
  }
  return null;
} // toDateStr()

/**
 * Reads a caller-supplied as-of date. 'YYYY-MM-DD' exactly, or null.
 *
 * STRICTER THAN `toDateStr`, AND THE ASYMMETRY IS THE WHOLE POINT — the two arguments are not the
 * same kind of thing.
 *
 * A BOUND IS STORED DATA. It arrives from a document somebody else wrote, possibly before this
 * convention existed, so `toDateStr` truncates a longer ISO string and takes what it can read.
 *
 * AN AS-OF IS NOT DATA. Every one in this context is produced in-process, by `viewerToday`, at the
 * edge. Nothing stores one and nothing receives one from outside, so there is no legacy shape to
 * absorb and a malformed as-of is a programming error rather than a data condition.
 *
 * Which matters because truncating one is the exact conversion `toDateStr` refuses to perform on a
 * `Date`, and refuses for a reason that does not stop applying when the instant is serialized:
 * slicing `'2026-03-14T23:45:00.000Z'` yields `2026-03-14`, which is already the 15th in Tokyo. The
 * slice picks neither UTC nor local but whatever zone the string was written in, and then the
 * comparison against a bound is off by a day with nothing anywhere to show it. Refusing an instant
 * here is the same decision, applied consistently to both spellings of one.
 *
 * A null answer means the rules below cannot run, and they say so by refusing rather than guessing.
 *
 * @param {*} asOf - 'YYYY-MM-DD'
 * @returns {string|null}
 */
export function toAsOfDate(asOf) {
  return typeof asOf === 'string' ? readCalendarDate(asOf) : null;
} // toAsOfDate()

/**
 * Is a record with [start, end] bounds active on the given date? Both bounds are inclusive.
 *
 * NOT ACTIVE ON AN AS-OF THIS CANNOT READ, rather than active by default. An open-bounded record
 * would otherwise pass both tests and come back active for a date that was never supplied — the
 * safe direction is the one somebody notices, and a record missing from a list gets investigated
 * where a wrongly-included one gets billed.
 *
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {string|null} start
 * @param {string|null} end
 * @returns {boolean}
 */
export function isActiveAsOf(asOf, start, end) {
  const asOfDate = toAsOfDate(asOf);

  if (asOfDate === null) {
    return false;
  }
  const startStr = toDateStr(start);
  const endStr = toDateStr(end);

  if (startStr !== null && asOfDate < startStr) {
    return false;
  }
  if (endStr !== null && asOfDate > endStr) {
    return false;
  }
  return true;
} // isActiveAsOf()

/**
 * The calendar date `days` after the given one. Negative shifts backwards.
 *
 * The arithmetic is the shared kernel's, which works on UTC midnight so a DST transition cannot
 * land the shift on the wrong day. What this adds is reading a stored bound leniently, as `toDateStr`.
 *
 * Returns null for a bound `toDateStr` cannot read, on the same terms as everything else here —
 * shifting an unreadable date would invent one.
 *
 * @param {string|null|undefined} date - 'YYYY-MM-DD'
 * @param {number} days
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function addDays(date, days) {
  const dateStr = toDateStr(date);

  return dateStr === null ? null : addCalendarDays(dateStr, days);
} // addDays()

/**
 * How many days separate two calendar dates. Negative when `to` precedes `from`.
 *
 * INCLUSIVE OF NEITHER END — a difference, not a count of days covered. 2026-09-01 to 2026-09-08 is
 * 7 days apart and covers 8; a caller wanting the second adds one where that is being reasoned about.
 *
 * Null when either bound is one `toDateStr` cannot read: measuring against it would invent a span.
 *
 * @param {string|null|undefined} from - 'YYYY-MM-DD'
 * @param {string|null|undefined} to - 'YYYY-MM-DD'
 * @returns {number|null}
 */
export function daysBetween(from, to) {
  const fromStr = toDateStr(from);
  const toStr = toDateStr(to);

  return fromStr === null || toStr === null ? null : calendarDaysBetween(fromStr, toStr);
} // daysBetween()
